import { Col, Form, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import AuthWrapper from 'components/base/AuthWrapper';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { bankidStatus, bankidStatusOsoba, biLoginOvereni, biOvereniSms, biRedirectOvereni } from 'services/loginBankID';

import routePathEnum from 'enums/routePathEnum';

interface IOvereniBankID {}

const OvereniBankID: React.FC<IOvereniBankID> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [smsCodeErr, setSmsCodeErr] = useState<boolean>(false);
    const [smsResp, setSmsResp] = useState<{
        platnost: boolean;
        idosoby: number;
        stav: 'OK' | 'IDEN' | 'CHYBNY' | 'EXISTUJE';
    }>();
    const [userData, setUserData] = useState<{
        jmeno: string;
        prijmeni: string;
        token: string;
    }>();
    const [displayPage, setDisplayPage] = useState<'intro' | 'sms' | 'user' | 'success' | 'err'>('intro');
    const { hash } = useParams<{ hash: string }>();

    useEffect(() => {
        bankidStatus(hash!).then(resp => setSmsResp(resp));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const poslatSms = () => {
        setLoading(true);
        biOvereniSms(hash!)
            .catch(() => setDisplayPage('err'))
            .then(() => setDisplayPage('sms'))
            .finally(() => setLoading(false));
    };

    return (
        <AuthWrapper>
            <Title text="BANKID_OVERENI_LOGIN_TITLE" level={4} />
            {smsResp?.platnost ? (
                <>
                    {displayPage === 'intro' ? (
                        <LoadingWrapper loading={loading}>
                            <Paragraph
                                style={{
                                    display: 'block'
                                }}
                                text="BANKID_OVERENI_LOGIN_TEXT1"
                            />
                            {smsResp?.stav && (
                                <Paragraph
                                    style={{
                                        marginTop: 8,
                                        display: 'block'
                                    }}
                                    text={'BANKID_OVERENI_LOGIN_' + smsResp.stav}
                                />
                            )}
                            <Col>
                                <Row
                                    style={{
                                        marginTop: 26
                                    }}
                                >
                                    <ButtonComponent
                                        buttontext="ZASLAT_SMS_KOD"
                                        onClick={poslatSms}
                                        id="pxKlicPokracovatBtn"
                                    />
                                </Row>
                            </Col>
                        </LoadingWrapper>
                    ) : displayPage === 'sms' ? (
                        <>
                            <Paragraph text="BANKID_OVERENI_LOGIN_TEXT2" />
                            <div
                                style={{
                                    margin: 10
                                }}
                            >
                                <FormWrapper
                                    onFinish={vals => {
                                        setSmsCodeErr(false);
                                        setLoading(true);
                                        biLoginOvereni({
                                            hash: hash!,
                                            sms: vals.kod
                                        })
                                            .then(resp => {
                                                if (!resp.token) {
                                                    setSmsCodeErr(true);
                                                    setLoading(false);
                                                } else {
                                                    bankidStatusOsoba(resp.token)
                                                        .then(data => {
                                                            setUserData({
                                                                ...data,
                                                                token: resp.token
                                                            });
                                                            form.setFieldsValue(data);
                                                            setDisplayPage('user');
                                                        })
                                                        .finally(() => setLoading(false));
                                                }
                                            })
                                            .catch(() => {
                                                setSmsCodeErr(true);
                                                setLoading(false);
                                            });
                                    }}
                                >
                                    <InputComponent label="CODE" name="kod" type="text" />
                                    <Row>
                                        <ButtonComponent
                                            buttontext="POKRACOVAT"
                                            id="pxKlicPokracovatBtn"
                                            htmlType="submit"
                                            loading={loading}
                                            extramessage={smsCodeErr ? 'SMS_INVALID' : ''}
                                        />
                                    </Row>
                                </FormWrapper>
                            </div>
                            <Row>
                                <TranslateText>SMS_RESEND_TXT</TranslateText>
                                <ButtonComponent
                                    id="sendSMSbtn"
                                    style={{
                                        marginTop: -8,
                                        marginLeft: 10
                                    }}
                                    htmlType="button"
                                    type="primary"
                                    buttontext="SMS_RESEND_BTN"
                                    loading={loading}
                                    onClick={poslatSms}
                                />
                            </Row>
                        </>
                    ) : displayPage === 'user' ? (
                        <FormWrapper onFinish={() => {}} outForm={form}>
                            <InputComponent label="JMENO" name="jmeno" value={userData?.jmeno} disabled={true} />
                            <InputComponent
                                label="PRIJMENI"
                                name="prijmeni"
                                value={userData?.prijmeni}
                                disabled={true}
                            />
                            <Paragraph
                                style={{
                                    display: 'block',
                                    position: 'relative',
                                    top: -8,
                                    marginTop: 10
                                }}
                                text="BANKID_OVERENI_LOGIN_TEXT3"
                            />
                            <ButtonComponent
                                id="sendSMSbtn"
                                htmlType="button"
                                type="primary"
                                buttontext="BANKID_OVERENI_LOGIN_BTN"
                                loading={loading}
                                onClick={() => {
                                    setLoading(true);
                                    biRedirectOvereni(userData?.token!)
                                        .then(rezp => window.location.replace(rezp.redirect))
                                        .catch(() => setLoading(false));
                                }}
                            />
                        </FormWrapper>
                    ) : (
                        <>
                            <Paragraph
                                style={{
                                    display: 'block',
                                    marginTop: 20
                                }}
                                text="BANKID_OVERENI_LOGIN_SUCC"
                            />
                            <ButtonComponent
                                id="sendSMSbtn"
                                htmlType="button"
                                type="primary"
                                buttontext="BANKID_OVERENI_LOGIN_SUCC_BTN"
                                loading={loading}
                                onClick={() => {
                                    navigateWorkaround(routePathEnum.LOGIN_ROZCESTNIK);
                                }}
                            />
                        </>
                    )}
                </>
            ) : (
                <>
                    <Title level={5} text="PRAVO_ZADOST_EXPIRED" />
                    {smsResp?.stav && (
                        <Paragraph
                            style={{
                                display: 'block'
                            }}
                            text={'BANKID_OVERENI_LOGIN_' + smsResp.stav}
                        />
                    )}
                </>
            )}
        </AuthWrapper>
    );
};

export default OvereniBankID;
